import React, { useEffect, useMemo, useState } from 'react';
import styles from './Style.module.css';
import { buildPaxesParam, formatToSlotLabel, useSticky } from '../../Utils/Helper';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../Components/Header/Header';
import Timer from '../../Components/Timer/Timer';
import { Cart } from '../../Components/Cart/Cart';
import { AppointmentSummary } from '../../Components/AppointmentSummary/AppointmentSummary';
import { SessionExpiredPopup } from '../../Components/Popup/SessionExpiredPopup/SessionExpiredPopup';
import { SlotUnavailablePopup } from '../../Components/Popup/SlotUnavailablePopup/SlotUnavailablePopup';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { useHoldExpiry } from '../../Utils/Hooks/useHoldExpiry';
import useBookingTimer from '../../Context/Timer/useBookingTimer';
import { LeaveBookingPopup } from '../../Components/Popup/LeaveBookingPopup/LeaveBookingPopup';
import { useValidateBookingSession } from '../../Utils/Hooks/useValidateBookingSession';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useCartDisplayTotal } from '../../Utils/Hooks/useCartDisplayTotal';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import { useCustomer } from '../../Context/Customer/CustomerContext';
import { useLocation as useLocationContext } from '../../Context/Location/LocationContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { Footer } from '../../Components/Cart/Footer';
import { Snakbar } from '../../Components/Snakbar/Snakbar';
import { getBookingErrorMessage, useCreateBookingRequest } from '../../Utils/Hooks/useCreateBookingRequest';
import { useCheckBookingSlots, useCheckSlotAvailable } from '../../APICalling/useApiQueries';

import { canSkipBookingVerification } from "../../Utils/bookingVerification";
import { navigateWithInputFocus } from "../../Utils/navigateWithInputFocus";

export const ReviewAndConfirmBooking = () => {
  useValidateBookingSession();
  const { bookingID, uuid, setUuid, bookingUpdate } = useBookingSession();
  const { selectedServices, members } = useGuests();
  const { membership } = useAppointment();
  const { userID, formData, isUserVerified, verifiedMobile } = useCustomer();
  const { selectedOutletID } = useLocationContext();
  const { selectedDate, selectedTime, setSlotPublicHoliday } = useSchedule();
  const cartDisplayTotal = useCartDisplayTotal(members, membership);
  const navigate = useNavigate();
  const { isTimesup, handleSessionExpiredClose, handleLeaveBooking } = useHoldExpiry();
  const { isTimerActive, timeLeft, isLeaveBookingPopupActive, setIsLeaveBookingPopupActive, resetTimer } = useBookingTimer();
  const { isSticky, sentinelRef } = useSticky();
  const [error, setError] = useState("");
  const [slotUnavailableMessage, setSlotUnavailableMessage] = useState("");

  const { createBooking, isPending: isUpdateBookingPending } = useCreateBookingRequest({
    onSuccess: (response) => {
      if (response?.data?.success) {
        resetTimer();
        setUuid(null);
        navigate(`/appointment/${uuid}`);
        return;
      }
      setError(getBookingErrorMessage(response));
    },
    onError: (err) => {
      setError(getBookingErrorMessage(err?.response?.data || err));
    },
  });

  const checkSlotsMutation = useCheckBookingSlots();
  const checkSlotAvailableMutation = useCheckSlotAvailable();

  const slotAvailablePayload = useMemo(() => ({
    panel: "customer",
    outlet: selectedOutletID,
    start_date: selectedDate,
    end_date: selectedDate,
    slot: formatToSlotLabel(selectedTime),
    paxes: buildPaxesParam(members),
    ...(uuid && { uuid }),
  }), [selectedOutletID, selectedDate, selectedTime, members, uuid]);

  const isConfirmPending = isUpdateBookingPending || checkSlotsMutation.isPending || checkSlotAvailableMutation.isPending;

  const checkSlotsPayload = useMemo(() => ({
    panel: "customer",
    outlet: selectedOutletID,
    start_date: selectedDate,
    end_date: selectedDate,
    slot: formatToSlotLabel(selectedTime),
    paxes: buildPaxesParam(members),
    uuid: uuid,
  }), [selectedOutletID, selectedDate, selectedTime, members, uuid]);

  const handleNext = async () => {
    if (isTimesup || checkSlotAvailableMutation.isPending) return;
    setError("");
    try {
      const response = await checkSlotAvailableMutation.mutateAsync(slotAvailablePayload);
      if (response?.success === false || response?.data?.available === false) {
        setSlotUnavailableMessage(response?.message || "This slot is no longer available. Please choose another slot/date.");
        return;
      }
      setSlotPublicHoliday(selectedDate, selectedTime, response?.data?.is_public_holiday ?? false);
    } catch (err) {
      const errData = err?.response?.data;
      if (errData?.slot_not_available || errData?.data?.available === false) {
        setSlotUnavailableMessage(errData?.message || "This slot is no longer available. Please choose another slot/date.");
        return;
      }
      setError("Unable to check slot availability. Please try again.");
      return;
    }
    navigateWithInputFocus(navigate, "/verify-identity", "booking-otp-mobile");
  };


  useEffect(() => {
    if (isTimesup || isLeaveBookingPopupActive || slotUnavailableMessage) {
      document.body.classList.add('overlay', 'overflow-hidden', 'overlay-index');
    } else {
      document.body.classList.remove('overlay', 'overflow-hidden', 'overlay-index');
    }
  }, [isTimesup, isLeaveBookingPopupActive, slotUnavailableMessage]);

  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [error]);

  const handleConfirmAndUpdateBooking = () => {
    if (isTimesup || isConfirmPending) return;
    setError("");
    checkSlotsMutation.mutate(checkSlotsPayload, {
      onSuccess: (response) => {
        if (response?.slot_not_available || response?.data?.available === false) {
          setSlotUnavailableMessage(response?.message || "This slot is no longer available. Please choose another slot/date.");
          return;
        }
        if (response?.success === false || response?.data?.success === false) {
          setError(getBookingErrorMessage(response));
          return;
        }
        checkSlotAvailableMutation.mutateAsync(slotAvailablePayload).then((slotResponse) => {
          if (slotResponse?.success === false || slotResponse?.data?.available === false) {
            setSlotUnavailableMessage(slotResponse?.message || "This slot is no longer available. Please choose another slot/date.");
            return;
          }
          const isPublicHoliday = slotResponse?.data?.is_public_holiday ?? false;
          setSlotPublicHoliday(selectedDate, selectedTime, isPublicHoliday);
          createBooking({
            userId: userID,
            action: "update",
            bookingId: bookingID ?? "",
            isPublicHoliday,
          });
        }).catch((err) => {
          const errData = err?.response?.data;
          if (errData?.slot_not_available || errData?.data?.available === false) {
            setSlotUnavailableMessage(errData?.message || "This slot is no longer available. Please choose another slot/date.");
            return;
          }
          // Non-blocking for other errors: proceed with booking
          createBooking({
            userId: userID,
            action: "update",
            bookingId: bookingID ?? "",
          });
        });
      },
      onError: (err) => {
        const errData = err?.response?.data;
        if (errData?.slot_not_available || errData?.data?.available === false) {
          setSlotUnavailableMessage(errData?.message || "This slot is no longer available. Please choose another slot/date.");
          return;
        }
        setError(getBookingErrorMessage(errData || err));
      },
    });
  };

  return (
    <>
    <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
    <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
      <Timer customClass={styles.timer} />
      <Header 
        title="Review and Confirm"
      />
    </div>
    <div className={`${styles.reviewAndConfirmBooking} container-md space-x`}>
    <AppointmentSummary 
    noTitle={true}
    noServicePrice={true}
    noAddonsPrice={true}
    noPriceDetails={true}
    noBookingID={true}
    />
    </div>
    <Snakbar
      text={error}
      active={error}
      varient="danger"
      type="error"
      hasButton={false}
    />
    {
    bookingUpdate ? 
    <Footer
    btnLabel={isConfirmPending ? "Processing..." : "Confirm & Update Booking"}
    btnClick={handleConfirmAndUpdateBooking}
    isFooterActive={true}
    noTerms={true}
    disabled={isTimesup || isConfirmPending}
    />
    :
    <Cart
      btnLabel={checkSlotAvailableMutation.isPending ? "Checking..." : "Next"}
      btnClick={handleNext}
      isCartActive={true}
      disabled={isTimesup || checkSlotAvailableMutation.isPending}
      displayTotal={cartDisplayTotal}
      displayCount={selectedServices.length}
      selectedServices={selectedServices}
    />
    }
    {isTimesup && (
      <SessionExpiredPopup
        active={isTimesup}
        onClose={handleSessionExpiredClose}
      />
    )}
    {isLeaveBookingPopupActive && !bookingUpdate && (
      <LeaveBookingPopup
        active={isLeaveBookingPopupActive}
        onClose={() => setIsLeaveBookingPopupActive(false)}
        handleDeleteBooking={handleLeaveBooking}
      />
    )}
    <SlotUnavailablePopup
      active={!!slotUnavailableMessage}
      message={slotUnavailableMessage}
      onClose={() => setSlotUnavailableMessage("")}
    />
    </>
  )
}
